/**
 * Defines the opening and closing hours of the restaurant for each day of the week
 */
export class RestaurantHours {
	mon: RestaurantHourOptions[];
	tue: RestaurantHourOptions[];
	wed: RestaurantHourOptions[];
	thu: RestaurantHourOptions[];
	fri: RestaurantHourOptions[];
	sat: RestaurantHourOptions[];
	sun: RestaurantHourOptions[];

	constructor() {
		this.mon = [];
		this.tue = [];
		this.wed = [];
		this.thu = [];
		this.fri = [];
		this.sat = [];
		this.sun = [];
	}

	/** 
	 * Adds the restaurant opening hour and closing hour to a day's array (as you can have multiple opening/closing hours in a single day)
	 * 
	 * @param day Number representing the day of the week where you want to set: monday is 0, sunday is 6
	 * @param hours what time the restaurant opens and closes
	 */
	setRestaurantHours(day: Number, hours: RestaurantHourOptions[]) {
		switch (day) {
			case 0:
				this.mon = hours;
				break;
			case 1:
				this.tue = hours;
				break;
			case 2:
				this.wed = hours;
				break;
			case 3:
				this.thu = hours;
				break;
			case 4:
				this.fri = hours;
				break;
			case 5:
				this.sat = hours;
				break;
			case 6:
				this.sun = hours;
				break;
			default:
				console.error("Unknown day of the week.");
				return;
		}
	}

	updateHour(day: Number, isOpeningTime: boolean, hour: string) {
		switch (day) {
			case 0:
				if (isOpeningTime) {
					this.mon[0].open = hour;
				} else {
					this.mon[0].close = hour;
				}
				break;

			case 1:
				if (isOpeningTime) {
					this.tue[0].open = hour;
				} else {
					this.tue[0].close = hour;
				}
				break;

			case 2:
				if (isOpeningTime) {
					this.wed[0].open = hour;
				} else {
					this.wed[0].close = hour;
				}
				break;

			case 3:
				if (isOpeningTime) {
					this.thu[0].open = hour;
				} else {
					this.thu[0].close = hour;
				}
				break;

			case 4:
				if (isOpeningTime) {
					this.fri[0].open = hour;
				} else {
					this.fri[0].close = hour;
				}
				break;

			case 5:
				if (isOpeningTime) {
					this.sat[0].open = hour;
				} else {
					this.sat[0].close = hour;
				}
				break;

			case 6:
				if (isOpeningTime) {
					this.sun[0].open = hour;
				} else {
					this.sun[0].close = hour;
				}
				break;

			default:
				console.error("Unknown day of the week.");
				return;
		}
	}

	/** Converts this object into a format that can be saved on our database (DynamoDB) */
	toAWSObject() {
		let mondayArray: any = [];
		this.mon.forEach(element => {
			mondayArray.push(element)
		});
		let tuesdayArray: any = [];
		this.tue.forEach(element => {
			tuesdayArray.push(element)
		});
		let wednesdayArray: any = [];
		this.wed.forEach(element => {
			wednesdayArray.push(element)
		});
		let thursdayArray: any = [];
		this.thu.forEach(element => {
			thursdayArray.push(element)
		});
		let fridayArray: any = [];
		this.fri.forEach(element => {
			fridayArray.push(element)
		});
		let saturdayArray: any = [];
		this.sat.forEach(element => {
			saturdayArray.push(element)
		});
		let sundayArray: any = [];
		this.sun.forEach(element => {
			sundayArray.push(element)
		});

		let hoursObj: any = {};
		hoursObj["mon"] = mondayArray;
		hoursObj["tue"] = tuesdayArray;
		hoursObj["wed"] = wednesdayArray;
		hoursObj["thu"] = thursdayArray;
		hoursObj["fri"] = fridayArray;
		hoursObj["sat"] = saturdayArray;
		hoursObj["sun"] = sundayArray;

		return hoursObj;
	}

	static generateDefaultRestaurantHours() {
		const newRestaurantHours = new RestaurantHours();
		newRestaurantHours.setRestaurantHours(0, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(1, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(2, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(3, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(4, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(5, [new RestaurantHourOptions("11:30", "23:00")]);
		newRestaurantHours.setRestaurantHours(6, [new RestaurantHourOptions("11:30", "23:00")]);

		return newRestaurantHours;
	}

	static newFromAws(awsItem: any): RestaurantHours {
		if (awsItem) {
			let mondayHours: RestaurantHourOptions[] = [];
			let tuesdayHours: RestaurantHourOptions[] = [];
			let wednesdayHours: RestaurantHourOptions[] = [];
			let thursdayHours: RestaurantHourOptions[] = [];
			let fridayHours: RestaurantHourOptions[] = [];
			let saturdayHours: RestaurantHourOptions[] = [];
			let sundayHours: RestaurantHourOptions[] = [];

			if (awsItem.mon != null) {
				awsItem.mon.forEach((element: any) => {
					mondayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.tue != null) {
				awsItem.tue.forEach((element: any) => {
					tuesdayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.wed != null) {
				awsItem.wed.forEach((element: any) => {
					wednesdayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.thu != null) {
				awsItem.thu.forEach((element: any) => {
					thursdayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.fri != null) {
				awsItem.fri.forEach((element: any) => {
					fridayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.sat != null) {
				awsItem.sat.forEach((element: any) => {
					saturdayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}
			if (awsItem.sun != null) {
				awsItem.sun.forEach((element: any) => {
					sundayHours.push(new RestaurantHourOptions(element.open, element.close))
				});
			}

			const newRestaurantHours = new RestaurantHours();
			newRestaurantHours.setRestaurantHours(0, mondayHours);
			newRestaurantHours.setRestaurantHours(1, tuesdayHours);
			newRestaurantHours.setRestaurantHours(2, wednesdayHours);
			newRestaurantHours.setRestaurantHours(3, thursdayHours);
			newRestaurantHours.setRestaurantHours(4, fridayHours);
			newRestaurantHours.setRestaurantHours(5, saturdayHours);
			newRestaurantHours.setRestaurantHours(6, sundayHours);
			return newRestaurantHours;
		} else {
			return this.generateDefaultRestaurantHours();
		}
	}
}

//=================================================================================================================\\

/**
 * Object containing the opening and closing hours for a restaurant
 * For the restaurant hours, we consider 23:59 as the end of the day, and 00:00 as the beginning of the day.
 */
export class RestaurantHourOptions {
	open: string;
	close: string;

	/**
	 * Constructor
	 * @param openingHour What time the restaurant opens
	 * @param closingHour  What time the restaurant closes
	 */
	constructor(openingHour: string, closingHour: string) {
		this.open = openingHour;
		this.close = closingHour;
	}

	toAWSObject() {
		return {
			"open": this.open,
			"close": this.close
		}
	}
}